import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { StarterDashboardPageCombinedState, StarterDashboardPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { StarterDashboardPageDataContextProvider } from './pageData';
import { StarterDashboardBannerStateProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<StarterDashboardPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: StarterDashboardPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function StarterDashboardPageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<StarterDashboardPageDataContextProvider pageData={pageData}>
			<StarterDashboardBannerStateProvider>
				<TrackingContext properties={{ petition_id: `petition_${pageData.data.petition.id}` }}>
					{children}
				</TrackingContext>
			</StarterDashboardBannerStateProvider>
		</StarterDashboardPageDataContextProvider>
	);
}
